(ns app.front.view.landing-page
  "Landing page view for municipality-branded URLs.
   This page is shown when the user visits /:branding/ directly."
  (:require [app.domain.custom-link.model :as custom-link-model]
            [app.domain.group.model :as group-model]
            [app.domain.group.permit-type-config.model :as ptc-model]
            [app.domain.util :as du]
            [app.front.branding]
            [app.front.entities :as entities]
            [app.front.root :as root]
            [app.front.ui :as ui]
            [app.front.ui.cards :as cards]
            [app.front.ui.chat-input :as chat-input]
            [app.front.ui.chat-panel :as chat-panel]
            [app.front.ui.dropdown :as ui-dd]
            [app.front.ui.top-bar :as top-bar]
            [app.front.util :as futil]
            [app.front.view.application-wizard.core :as application-wizard]
            [app.front.view.login :as login]
            [app.i18n.model :as i18n-model]
            [app.rich-text :as rich-text]
            [clojure.string :as str]
            [core.front.carbon :as cc]
            [core.front.comm]
            [core.front.event :refer [<sub >evt]]
            [core.front.feature-flags :as ff]
            [core.front.href :as href]
            [core.front.i18n :as i18n]
            [core.front.icons :as i]
            [core.front.media-query :as media-query]
            [core.util.util :as util]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]
            [reagent.core :as r]))

;;
;; Landing Card Display Data
;;

(defn permit-type-config-title
  [ctx {:permit-type-config/keys [permit-type] :as permit-type-config} lang]
  (let [group-id (-> permit-type-config :group/_permit-type-configs :db/id)
        names    (->> {:group.config/group  group-id
                       :group.config/target permit-type}
                      (entities/find-entity ctx)
                      :group.config/names)]
    (i18n-model/get-text-from-entities-or-map names lang)))

(defn permit-type-config-municipality
  [{:permit-type-config/keys [permit-type] :as permit-type-config}]
  (-> permit-type-config
      :group/_permit-type-configs
      :group/scopes
      (p/find-first {:scope/permit-type permit-type})
      :scope/municipality))

(defn- open-sign-in-overlay!
  [redirect-path]
  (>evt [::login/init])
  (>evt [::login/fetch-public-login-providers])
  (>evt [::login/open-sign-in-overlay redirect-path]))

(defn permit-type-card-data
  [ctx lang tr logged-in? application-wizard? ptcs]
  (->> ptcs
       (filter ptc-model/application-permit-type?)
       (sort-by #(-> % :permit-type-config/permit-type name))
       (mapv (fn [{:permit-type-config/keys [permit-type] :as ptc}]
               (let [authority-group                  (:group/_permit-type-configs ptc)
                     scopes                           (group-model/scopes-for-permit-type authority-group permit-type)
                     multi-municipality?              (> (count scopes) 1)
                     {:municipality/keys [country
                                          subdivision
                                          number]}    (when-not multi-municipality?
                                                        (:scope/municipality (first scopes)))
                     title                            (or (permit-type-config-title ctx ptc lang)
                                                          (tr [permit-type]))
                     content                          (or (ptc-model/permit-type-config-description ptc lang)
                                                          (tr [(keyword "application-type.description" permit-type)]))
                     description                      (rich-text/html-to-text content)
                     img                              (ptc-model/permit-type->icon-path permit-type)
                     href                             (if application-wizard?
                                                        ;; For single-municipality groups, pass municipality + country so
                                                        ;; the wizard goes straight to the single-municipality path.
                                                        ;; For multi-municipality groups, omit municipality/country so the
                                                        ;; wizard loads config by authority group and shows a municipality
                                                        ;; picker at the location step.
                                                        (href/href :view/application-wizard
                                                                   {:step application-wizard/first-step-path}
                                                                   (cond-> {:permit-type permit-type
                                                                            :group-id    (:db/id authority-group)}
                                                                     (not multi-municipality?)
                                                                     (assoc :municipality number
                                                                            :country      country)))
                                                        (if multi-municipality?
                                                          (href/href :create-application-v2
                                                                     {:part 1}
                                                                     (util/mmap {:authority-group-id (:db/id authority-group)}
                                                                                permit-type))
                                                          (href/href :create-application-v2
                                                                     {:part 1}
                                                                     (-> {:municipality number}
                                                                         (util/mmap subdivision permit-type)
                                                                         (update :subdivision name)))))
                     on-click                         (when-not logged-in? #(open-sign-in-overlay! href))
                     action-text                      (tr [:apply-button])]
                 (util/mmap title img href on-click action-text content description subdivision number))))))

(def ^:private municipality-name-token "{Municipality Name}")

(defn- substitute-municipality-name
  [text municipality-name]
  (if (and text municipality-name)
    (str/replace text municipality-name-token municipality-name)
    text))

(defn- localized-description
  "Resolve a localized rich-text description map (`{lang-keyword html-string}`)
   for the given `lang`, convert it to plaintext for tile display, and
   substitute the `{Municipality Name}` token with `municipality-name`.
   Falls back to `default-text` (already plaintext) when the configured
   description is empty."
  [descriptions lang municipality-name default-text]
  (-> (or (some-> (i18n-model/get-text-from-entities-or-map descriptions lang)
                  rich-text/html-to-text
                  not-empty)
          default-text)
      (substitute-municipality-name municipality-name)))

(defn public-records-card-data
  [tr public-records-tabs & [description]]
  (cond-> []
    (seq public-records-tabs)
    (conj (p/assoc-if {:title       (tr [:public-records/public-records])
                       :href        (href/href :view/public-records)
                       :type        :link
                       :img         "/asset/img/landing-v2-icon-public-records.svg"
                       :action-text (tr [:public-records.public-records])}
                      :description description))
    (:public-records/contractor-registrations public-records-tabs)
    (conj {:title       (tr [:public-records.contractor-search])
           :href        (href/href :view/public-records {} {:tab :contractor-registrations})
           :type        :link
           :img         "/asset/img/landing-v2-icon-contractor-registration-search.svg"
           :action-text (tr [:public-records.contractor-search])})))

(defn complaints-card-data
  [tr & [description display-names lang]]
  [(p/assoc-if {:title       (or (some-> (or (get display-names lang)
                                             (get display-names (i18n-model/get-base-lang lang)))
                                         str/trim
                                         not-empty)
                                 (tr [:public-records/file-complaint]))
                :type        :link
                :img         "/asset/img/landing-v2-icon-complaints.svg"
                :href        (href/href :view/create-complaint)
                :action-text (tr [:public-map-view.complaint-submission])}
               :description description)])

(defn contact-request-card-data
  [tr domain-group-id & [description]]
  [(p/assoc-if {:title       (tr [:public-map-view/contact-request])
                :type        :button
                :img         "/asset/img/landing-v2-icon-contact.svg"
                :on-click    #(>evt [:app.front.ui.contact-request-panel/toggle-open (util/mmap domain-group-id)])
                :action-text (tr [:public-map-view.contact-request])}
               :description description)])


;;
;; Subscriptions from app.front.branding
;;

(rf/reg-sub ::branding-config
  :-> :branding/config)

(rf/reg-sub ::branding-images
  :-> :branding/images)

(rf/reg-sub ::branding-loading?
  :-> :branding/loading?)


;;
;; Subscriptions
;;

(rf/reg-sub ::landing-page-branding
  :-> ::landing-page-branding)

(rf/reg-sub ::landing-config
  :-> ::landing-config)

(rf/reg-sub ::ready?
  :<- [::branding-loading?]
  :<- [:comm/command-processing? :group/get-landing-config]
  :-> (fn [[bc? lc?]]
        (not (or bc? lc?))))

(rf/reg-sub ::landing-group
  :<- [::entities/context]
  :<- [::ready?]
  :<- [::landing-page-branding]
  :-> (fn [[ctx ready? brand-slug]]
        (when ready?
          (entities/find-entity ctx {:group/uri-path-id brand-slug}))))

(rf/reg-sub ::municipality-name
  :<- [::landing-group]
  :-> :group/name)

(rf/reg-sub ::public-records-card-data
  :<- [:tr]
  :<- [::ready?]
  :<- [::landing-config]
  :<- [:language]
  :<- [::municipality-name]
  :-> (fn [[tr ready? {:keys [public-records-tabs public-records-description]} lang muni-name]]
        (when (and ready? (seq public-records-tabs))
          (public-records-card-data tr public-records-tabs
                                    (localized-description public-records-description lang muni-name
                                                           (tr [:landing-page/default-public-records-description] [muni-name]))))))

(rf/reg-sub ::permit-type-configs
  :<- [::entities/context]
  :<- [::ready?]
  :<- [::landing-page-branding]
  :-> (fn [[ctx ready? brand-slug]]
        (when ready?
          (->> (entities/find-entity ctx {:group/uri-path-id brand-slug})
               group-model/descendant-authority-groups
               (mapcat group-model/permit-type-configs)))))

(rf/reg-sub ::complaint-card-data
  :<- [:tr]
  :<- [::landing-config]
  :<- [:language]
  :<- [::municipality-name]
  :-> (fn [[tr {:keys [complaint-description complaint-display-name show-complaints? complaint-submission?]}
            lang muni-name]]
        (when (and show-complaints? complaint-submission?)
          (complaints-card-data tr (localized-description complaint-description lang muni-name
                                                          (tr [:landing-page/default-complaint-description] [muni-name]))
                                complaint-display-name lang))))

(rf/reg-sub ::contact-request-card-data
  :<- [:tr]
  :<- [::landing-config]
  :<- [::landing-group]
  :<- [:language]
  :<- [::municipality-name]
  (fn [[tr landing-config landing-group lang muni-name]]
    (when (:contact-request? landing-config)
      (contact-request-card-data tr (:group/id landing-group)
                                 (localized-description (:contact-request-description landing-config) lang muni-name
                                                        (tr [:landing-page/default-contact-request-description]
                                                            [muni-name]))))))

(rf/reg-sub ::custom-link-card-data
  :<- [::landing-config]
  :<- [:language]
  (fn [[landing-config lang]]
    (when-let [custom-links (seq (:custom-links landing-config))]
      (mapv (fn [{:custom-link/keys [titles descriptions url icon]}]
              {:href             url
               :type             :link
               :open-in-new-tab? true
               :title            (i18n-model/get-text-from-entities-or-map titles lang)
               :description      (i18n-model/get-text-from-entities-or-map descriptions lang)
               :img              (custom-link-model/custom-link-icon->img (or icon
                                                                              custom-link-model/default-custom-link-icon))})
            custom-links))))

(rf/reg-sub ::permit-type-card-data
  :<- [::entities/context]
  :<- [:language]
  :<- [:tr]
  :<- [:session/logged-in?]
  :<- [::ff/enabled? :feature-flag.id/application-wizard]
  :<- [::permit-type-configs]
  :-> (fn [[ctx lang tr logged-in? application-wizard? ptcs]]
        (permit-type-card-data ctx lang tr logged-in? application-wizard? ptcs)))

(rf/reg-sub ::group-type
  (fn [db _]
    (-> db ::landing-config :group-type)))

(rf/reg-sub ::member-municipalities
  (fn [db _]
    (-> db ::landing-config :member-municipalities)))

(rf/reg-sub ::landing-config-languages
  (fn [db _]
    (-> db ::landing-config :languages)))

(rf/reg-sub ::landing-languages
  :<- [::i18n/locales]
  :<- [::landing-config-languages]
  (fn [[all-locales langs] _]
    (if (seq langs)
      (select-keys all-locales langs)
      all-locales)))

;;
;; Events
;;

(rf/reg-event-fx ::init
  (fn [{:keys [db]} [_ branding]]
    (when (or (not= branding (::landing-page-branding db))
              (nil? (::landing-config db)))
      {:db (-> db
               (assoc ::landing-page-branding branding)
               (dissoc ::landing-config))
       :fx [[:dispatch [:comm/command-monitored
                        {:command    :group/get-landing-config
                         :method     :get
                         :data       {:brand branding}
                         :on-success [::init.success]}]]]})))

(rf/reg-event-fx ::init.success
  (fn [{:keys [db]} [_ _ config]]
    (let [db (cond-> (assoc db ::landing-config config)
               (-> config :landing :entities)
               (entities/merge-entity-db (-> config :landing :entities)))]
      (cond-> {:db db}
        (:default-language config)
        (assoc :fx [[:dispatch [::i18n/change-language (:default-language config)]]])))))


;;
;; Landing page header
;;

(defn- landing-header-button
  "A button styled for the landing page header.
   Mobile: transparent, evenly distributed section. Desktop: pill button."
  [{:keys [text icon on-click href blank?]}]
  (let [classes ["flex flex-row items-center justify-center gap-2 flex-1 py-3"
                 "lg:flex-initial lg:px-3 lg:py-2 lg:rounded"
                 "lg:bg-ui-night-sky-100 lg:hover:bg-ui-night-sky-80"
                 "cursor-pointer"
                 "text-white text-sm whitespace-nowrap"
                 "transition-colors no-underline"]
        content [:<>
                 (when icon
                   (cc/icon {:icon        icon
                             :description text
                             :fill        "currentColor"}))
                 [:span text]]]
    (if href
      [:a (cond-> {:class classes :href href :aria-label text}
            blank? (assoc :rel "noopener" :target "_blank"))
       content]
      [:button {:class    (conj classes "border-0 bg-transparent")
                :on-click on-click}
       content])))

(defn- landing-language-dropdown-item [{:keys [text on-click]}]
  (cc/link
    {:class-name "landing-header__lang-item flex items-center px-4 py-2 text-sm cursor-pointer hover:bg-white/10 no-underline text-white whitespace-nowrap"
     :on-click   on-click}
    text))

(defn- landing-language-button [tr]
  (let [languages (<sub [::landing-languages])
        selected  (<sub [:language])]
    (when (du/at-least-two? languages)
      [ui-dd/dropdown
       {:class                  "flex flex-row self-stretch items-stretch flex-1 lg:flex-initial lg:bg-ui-night-sky-100 lg:hover:bg-ui-night-sky-80 cursor-pointer lg:rounded"
        :toggle-props           {:class "flex flex-row items-center justify-center gap-2 cursor-pointer text-white text-sm whitespace-nowrap transition-colors w-full py-3 lg:px-3 lg:py-2 lg:rounded"}
        :options-props          {:class "bg-ui-night-sky-100 text-white overflow-auto rounded"
                                 :style {:max-height "calc(100vh - 100px)"}}
        :open-icon-description  (tr [:open-menu])
        :close-icon-description (tr [:close-menu])}
       [:div.flex.flex-row.items-center.gap-2.landing-header__button--dropdown
        (cc/icon {:icon        i/world
                  :description (tr [:language/language])
                  :fill        "currentColor"})
        [:span.uppercase (get-in languages [selected :short-name] "")]]
       (futil/for-all [lang-key (keys languages)]
         ^{:key lang-key}
         [landing-language-dropdown-item
          {:text     (str (get-in languages [lang-key :name])
                          " (" (str/upper-case (get-in languages [lang-key :short-name])) ")")
           :on-click #(>evt [::i18n/change-language lang-key])}])])))

(defn- landing-support-button [tr]
  [landing-header-button {:text   (tr [:need-help])
                          :icon   i/circle-question
                          :href   (tr [:support-link])
                          :blank? true}])

(defn- landing-dashboard-button [tr]
  [landing-header-button {:text     (tr [:dashboard-link-text])
                          :icon     i/house
                          :on-click #(>evt [:route/push :dashboard])}])

(defn- landing-sign-in-button [tr]
  [landing-header-button {:text     (tr [:login-new/sign-in])
                          :icon     i/log-in
                          :on-click #(open-sign-in-overlay! "/dashboard")}])

(defn- landing-header-action-buttons
  "Action buttons shown in both large and small headers.
   Shows sign-in when not logged in, dashboard link when logged in."
  [tr logged-in?]
  [:<>
   [landing-language-button tr]
   [landing-support-button tr]
   (if logged-in?
     [landing-dashboard-button tr]
     [landing-sign-in-button tr])])

(defn landing-large-header
  "Large landing page header with background image, logo, title, and action buttons."
  [tr logged-in?]
  (let [branding-config (<sub [:branding/config])
        logo-url        (<sub [:branding/image-url :logo])
        branding-images (<sub [::branding-images])
        bg-url          (if (:landing-background branding-images)
                          (<sub [:branding/image-url :landing-background])
                          "/asset/img/Header-image.png")
        lang            (<sub [:language])
        title-text      (i18n-model/get-text-from-entities-or-map
                          (:group/branding-landing-page-titles branding-config) lang)
        subtitle-text   (i18n-model/get-text-from-entities-or-map
                          (:group/branding-landing-page-subtitles branding-config) lang)
        env             (top-bar/env-name)]
    [:<>
     [:header.landing-header.relative.flex.flex-col.items-center.justify-center
      {:style {"--landing-bg-url" (str "url(" bg-url ")")
               :min-height        "200px"}}
      [:div.flex.flex-col.flex-1.lg:flex-row.w-full.h-full.px-6.py-4.lg:absolute.lg:items-center
       ;; Logo + header text
       [:div.flex.flex-1.items-center.gap-4.min-w-0
        [:img.flex-shrink-0.w-28.lg:w-40
         {:src (or logo-url "/asset/img/Cloudpermit_logo_RGB_white.svg")
          :alt ""}]
        [:div.flex.flex-col
         (when (and title-text (not (str/blank? title-text)))
           [:h1.text-white.landing-header__header-text.m-0 title-text])
         (when (and subtitle-text (not (str/blank? subtitle-text)))
           [:h2.text-white.landing-header__header-text.m-0.text-lg subtitle-text])]
        (when (some-> env (not= "prod"))
          [:div.hidden.sm:block.items-center.uppercase
           [ui/tag {:type  :round
                    :size  :small
                    :color :blue}
            [:span.font-bold env]]])]
       ;; Action buttons — mobile: full-width evenly distributed sections with top border
       [:div.flex.items-center.w-full.lg:w-auto.lg:border-t-0.mt-4.lg:gap-2.pt-2.lg:pt-0.lg:self-end
        {:class "border-t border-solid border-white/20"}
        [landing-header-action-buttons tr logged-in?]]]]
     [login/sign-in-overlay]]))

(defn landing-small-header
  "Compact branded header bar for the landing page, styled like the standard app header."
  [tr logged-in?]
  [:<>
   [root/base-header
    tr
    ;; Action buttons
    [:div.flex.items-center.gap-2
     [landing-header-action-buttons tr logged-in?]]]
   [login/sign-in-overlay]])

(defn landing-header
  "Public header used by other public routes (public records, complaints, etc.).
   Always shows the large header with sign-in button (not logged in context)."
  [tr]
  [landing-large-header tr false])

(defn landing-header-root
  "Landing page header rendered at the root level. Shows large or small header
   based on branding config, works for both logged-in and anonymous users."
  [tr]
  (let [branding-config (<sub [::branding-config])
        logged-in?      (<sub [:session/logged-in?])
        large-header?   (let [v (:group/branding-landing-page-large-header? branding-config)]
                          (if (some? v) v true))]
    (if large-header?
      [landing-large-header tr logged-in?]
      [landing-small-header tr logged-in?])))


;;
;; View
;;

(defn- dashboard-button [tr]
  (cc/button {:kind     :primary
              :on-click #(>evt [:route/push :dashboard])}
             (tr [:dashboard-link-text])))

(defn- municipality-link
  "A card linking to a member municipality's landing page."
  [{:keys     [group/uri-path-id domain/subdivision logo-url]
    muni-name :group/name}]
  [:a.flex.items-center.gap-3.p-4.rounded.border.border-solid.border-gray-300.bg-white.no-underline.text-black.shadow-sm
   {:class "hover:shadow-md transition-shadow cursor-pointer w-72"
    :href  (str "/" uri-path-id "/")}
   [:img.flex-shrink-0 {:style {:height     "36px"
                                :width      "56px"
                                :object-fit "contain"}
                        :src   (or logo-url login/placeholder-logo)
                        :alt   ""}]
   [:div.flex.flex-col.min-w-0
    [:span.font-medium.truncate muni-name]
    (when subdivision
      [:span.text-sm.text-gray-600
       (last (str/split (name subdivision) #"-"))])]])

(defn- ai-chat-section
  "Search-bar-style input for starting an AI chat session."
  [tr]
  (let [panel-open? (<sub [::chat-panel/panel-open?])]
    [:section.border-none.rounded-none
     [:h2.text-xl.md:text-2xl.font-semibold.text-center.pb-4.md:pb-8.m-0
      (tr [:landing-page/ai-chat-heading])]
     [:div.flex.items-center.max-w-xl.mx-auto.pb-4
      [chat-input/chat-input
       {:id               "ai-chat-input"
        :placeholder      (tr [:landing-page/ai-chat-placeholder])
        :label-text       (tr [:landing-page/ai-chat-label])
        :send-description (tr [:chat/send])
        :on-submit        (fn [message]
                            (if panel-open?
                              (>evt [::chat-panel/send-message message])
                              (>evt [::chat-panel/toggle-open
                                     {:open?      true
                                      :on-success [::chat-panel/send-message message]}])))}]]]))

(defn- superdomain-view
  "Landing page view for superdomain groups — shows links to member municipalities."
  [tr logged-in? bg-url]
  (let [member-municipalities (<sub [::member-municipalities])]
    [:div.landing-page.flex.flex-col.items-center.p-8
     {:style {"--landing-bg-url" (str "url(" bg-url ")")}}
     [:div.landing-page__content.w-full {:style {:max-width "960px"}}
      [:h2.text-center.mb-6 (tr [:landing-page/select-municipality])]
      [:div.flex.flex-wrap.gap-4.justify-center
       (futil/for-all [{:keys [group/uri-path-id] :as muni} member-municipalities]
         ^{:key (str "muni-" uri-path-id)}
         [municipality-link muni])]
      (when logged-in?
        [:div.mt-8.flex.justify-center
         [dashboard-button tr]])]]))

(defn view []
  (let [tr                 (<sub [:tr])
        not-mobile?        (<sub [::media-query/breakpoint-matches? :sm])
        logged-in?         (<sub [:session/logged-in?])
        branding-config    (<sub [::branding-config])
        branding-images    (<sub [::branding-images])
        ready?             (<sub [::ready?])
        landing-group      (<sub [::landing-group])
        app-card-data      (<sub [::permit-type-card-data])
        pr-card-data       (<sub [::public-records-card-data])
        c-card-data        (<sub [::complaint-card-data])
        c-r-card-data      (<sub [::contact-request-card-data])
        cl-card-data       (<sub [::custom-link-card-data])
        title              (-> branding-config
                               :group/branding-login-info-title
                               (or (tr [:landing-page/welcome "Welcome"])))
        info               (:group/branding-login-info branding-config)
        group-type         (<sub [::group-type])
        bg-url             (if (:background branding-images)
                             (<sub [:branding/image-url :background])
                             "/asset/img/Cloudpermit_background.svg")
        other-card-data    (concat pr-card-data c-card-data c-r-card-data cl-card-data)
        combined-card-data (when (or (<= (count app-card-data) 1)
                                     (<= (count other-card-data) 1))
                             (concat app-card-data other-card-data))
        description-lines  (if not-mobile? 3 1)]

    (if (not ready?)
      [:div.landing-page.flex-1.flex.items-center.justify-center
       (cc/inline-loading {:description (tr [:loading "Loading..."])})]

      [:div.flex.flex-col.flex-1.min-h-0.h-full
       (if (= :group.type/superdomain group-type)
         [superdomain-view tr logged-in? bg-url]
         [:div.landing-page.flex.flex-col.items-center.sm:px-2.md:px-8.py-4
          {:style {"--landing-bg-url" (str "url(" bg-url ")")}}

          [:div.landing-page__content.text-center
           [:h1.text-2xl.md:text-4xl.font-bold.mt-4.mb-2.mx-auto.max-w-7xl.px-4 title]
           [:div.landing-page__body.mb-0.pb-0.mx-auto.max-w-7xl
            (when-not (util/nil-or-blank? info)
              [:div.text-xs.md:text-base.p-4
               {:class                   "[&_p]:leading-relaxed"
                :dangerouslySetInnerHTML (r/unsafe-html info)}])]

           [:div.landing-page__cards.flex.gap-4.m-4.justify-center.text-left
            [:div {:class "rounded border-solid border-[1px] border-gray-300 bg-white p-2 md:p-8 xl:max-w-[1280px]"}
             (when (and (ff/enabled? :feature-flag.id/ai-chat)
                        (not (group-model/ai-disabled? landing-group)))
               [:<>
                [ai-chat-section tr]
                [:hr.border-1.border-gray-100.my-2.md:my-4]])
             (if (some? combined-card-data)
               [cards/landing-cards (util/mmap {:cards combined-card-data} description-lines)]
               [:div
                [cards/landing-cards (util/mmap {:cards app-card-data} description-lines)]
                [:hr.border-1.border-gray-100.my-2.md:my-4]
                [cards/landing-cards (util/mmap {:cards other-card-data} description-lines)]])]]
           [:div.landing-page__actions.flex.gap-4.justify-center
            (when logged-in?
              [dashboard-button tr])]]])])))

(def route
  {:name          :view/landing-page
   :view          view
   :public?       true
   :own-layout?   true
   :custom-header landing-header-root
   :controllers   [{:parameters {:path [:branding]}
                    :start      (fn [{{:keys [branding]} :path}]
                                  (>evt [::init branding]))}]})
